
import React, { useEffect, useState } from "react";
import { Modal } from "./Modal";
import { UserPreferences, DEFAULT_PREFERENCES } from "../types/userPreferences";
import { getUserPreferences, saveUserPreferences } from "../services/userPreferences";
import { useAuth } from "../context/AuthContext";
import { useToast } from "./Toast";

const MODULE_OPTIONS = [
  { value: "dashboard", label: "法務總攬儀錶板" },
  { value: "tasksAndLogs", label: "待辦事項與法務工作日誌" },
  { value: "contracts", label: "合約系統管理" },
  { value: "legalMatters", label: "案件及爭議管理" },
  { value: "ipMatters", label: "智財申請與維權" },
  { value: "brandAssets", label: "品牌資產管理" },
  { value: "templates", label: "範本中心" },
];

const DATE_RANGE_OPTIONS = [
  { value: 7, label: "最近 7 天" },
  { value: 30, label: "最近 30 天" },
  { value: 90, label: "最近 90 天" },
  { value: 180, label: "最近半年" },
  { value: 365, label: "最近一年" },
];

const NOTIFY_MODULES = [
  { value: "contract", label: "合約管理" },
  { value: "case", label: "案件爭議" },
  { value: "seal", label: "用印申請" },
  { value: "govCorrespondence", label: "公文函覆" },
  { value: "task", label: "待辦事項" },
];

export function UserPreferencesModal({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [prefs, setPrefs] = useState<UserPreferences>(DEFAULT_PREFERENCES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.id) {
      getUserPreferences(user.id).then((p) => {
        setPrefs(p);
        setLoading(false);
      });
    }
  }, [user]);

  const handleSave = async () => {
    if (!user?.id) return;
    setSaving(true);
    try {
      await saveUserPreferences(user.id, prefs);
      showToast("偏好設定已儲存");
      onClose();
      // Optional: reload window to apply global module redirect changes if needed, 
      // but usually state update is enough.
    } catch (e) {
      showToast("儲存失敗", "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleNotify = (mod: string) => {
    const current = new Set(prefs.notifyModules);
    if (current.has(mod)) current.delete(mod);
    else current.add(mod);
    setPrefs({ ...prefs, notifyModules: Array.from(current) });
  };

  return (
    <Modal title="個人偏好設定" onClose={onClose} width={500}>
      {loading ? (
        <div className="p" style={{textAlign:"center", padding:20}}>載入中...</div>
      ) : (
        <div className="grid" style={{ gap: 20 }}>
          <div className="field">
            <label className="label">預設進入模組</label>
            <div className="p" style={{fontSize:12, marginBottom:6}}>登入系統後，優先進入的功能頁面。</div>
            <select 
              className="input" 
              value={prefs.defaultModule} 
              onChange={e => setPrefs({...prefs, defaultModule: e.target.value})}
            >
              {MODULE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div className="field">
            <label className="label">預設日期範圍</label>
            <div className="p" style={{fontSize:12, marginBottom:6}}>列表篩選、全域搜尋的預設時間跨度。</div>
            <select 
              className="input" 
              value={prefs.defaultDateRangeDays} 
              onChange={e => setPrefs({...prefs, defaultDateRangeDays: Number(e.target.value)})}
            >
              {DATE_RANGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div className="field">
            <label className="label">通知偏好</label>
            <div className="p" style={{fontSize:12, marginBottom:6}}>選擇您希望收到通知提醒的模組。</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, background:"rgba(255,255,255,0.05)", padding:10, borderRadius:8 }}>
              {NOTIFY_MODULES.map(m => (
                <label key={m.value} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor:"pointer" }}>
                  <input 
                    type="checkbox" 
                    checked={prefs.notifyModules.includes(m.value)} 
                    onChange={() => toggleNotify(m.value)}
                  />
                  {m.label}
                </label>
              ))}
            </div>
          </div>

          <div className="field">
             <label className="label">介面主題</label>
             <select className="input" disabled value="dark">
                <option value="dark">深色模式 (目前僅支援)</option>
             </select>
          </div>
        </div>
      )}

      <div className="row" style={{ marginTop: 24, justifyContent: "flex-end" }}>
        <button className="btn" style={{ width: "auto" }} onClick={handleSave} disabled={saving}>
          {saving ? "儲存中..." : "儲存設定"}
        </button>
      </div>
    </Modal>
  );
}
